import { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@/i18n';
import { shiftService } from '@/api/domains/shift';
import type { LC_BulkRejectShiftRequestsOut } from '@/api/domains/shift/shift.types';
import { useUI } from '@/hooks/ui/useUI';


interface UseShiftChangeRequestBulkRejectionParams {
  selectedRequestIds: number[];
  onSuccess: (result: LC_BulkRejectShiftRequestsOut) => void;
}

export interface UseShiftChangeRequestBulkRejectionResult {
  comment: string;
  setComment: (value: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
}

export function useShiftChangeRequestBulkRejection({
  selectedRequestIds,
  onSuccess,
}: UseShiftChangeRequestBulkRejectionParams): UseShiftChangeRequestBulkRejectionResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();

  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current || comment.trim().length === 0) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await shiftService.bulkRejectShiftRequests({
        requestIds: selectedRequestIds.join(','),
        approverComment: comment.trim(),
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:shiftChangeAdmin.messages.rejectError'));
        throw new Error('bulk-reject-failed');
      }

      const result = response.data;

      if (result.totalAlreadyResolved === 0) {
        showSuccess(translate('attendance:shiftChangeAdmin.bulk.rejectSuccess'));
      } else {
        showWarning(
          translate('attendance:shiftChangeAdmin.bulk.rejectPartialResult', {
            processed: result.totalRejected,
            skipped: result.totalAlreadyResolved,
          }),
        );
      }

      onSuccess(result);
      setComment('');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    comment,
    selectedRequestIds,
    showApiError,
    showSuccess,
    showWarning,
    translate,
    onSuccess,
  ]);

  return {
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleSubmit,
  };
}

export default useShiftChangeRequestBulkRejection;
